import React, { FunctionComponent, useEffect, useState, useRef } from "react";
import Dropdown from "rc-dropdown";
import Menu, { Item as MenuItem } from 'rc-menu';
import 'rc-dropdown/assets/index.css';
import { connect } from "react-redux";
import { IRootState } from "@utils";
import { delay } from "@utils/helpers";
import {
  VideoCallModalHeader,
  VideoCallModalTitle,
  VideoCallModalActionsWrapper,
  VideoCallModalBody,
  VideoCallModalActionButton,
  VideoCallModalDefaultStyle,
  VideoCallModalFullScreenStyle,
  VideoCallModalViewOption
} from "./videoCallModalStyles";
import {
  setVideoShow,
  setVideoNoShow,
  setVideoRoomMaximized,
  setVideoRoomMinimized,
  setRoomJoinedStatus
} from "@actions";
import { SDK } from "@lib/index";
import ReactModal from "react-modal";
import Draggable from "react-draggable";
// @ts-ignore
import ModalViewOptions from "@assets/icons/grip.svg";
// @ts-ignore
import ModalClose from "@assets/icons/modalClose.svg";
// @ts-ignore
import ModalShare from "@assets/icons/modalShare.svg";

interface IVideoCallModalProps {
  conversationId: string;
  phoneNumber: string;
  setVideoNoShow: typeof setVideoNoShow;
  setVideoShow: typeof setVideoShow;
  setVideoRoomMaximized: typeof setVideoRoomMaximized;
  setVideoRoomMinimized: typeof setVideoRoomMinimized;
  setRoomJoinedStatus: typeof setRoomJoinedStatus;
  app: IRootState["app"];
  agency: IRootState["agency"];
  show: boolean;
  $videoId: string;
  conversations: IRootState["conversations"];
}

const XVideoCallModal: FunctionComponent<IVideoCallModalProps> = ({
  app,
  conversations,
  conversationId,
  phoneNumber,
  agency,
  setVideoNoShow,
  setVideoRoomMaximized,
  setVideoRoomMinimized,
  setRoomJoinedStatus,
  show,
  $videoId
}: IVideoCallModalProps) => {
  const [fullScreen, setFullScreen] = useState(false);
  const currentConversation = conversations[conversationId];
  const modalRef = useRef(null);
  const [dragBounds, setDragBounds] = useState({ left: 0, right: 0, bottom: 0, top: 0 });

  const openShareModal = () => {
    if (app?.activeRoom?.conversation_id) {
      const shareButton = document.getElementById(`OpenVideoShare-${app.activeRoom.conversation_id}`);
      shareButton && shareButton.click();
    }
  }

  const onClose = () => {
    // @ts-ignore
    window.ConveyCurrentVideoRooms &&
      // @ts-ignore
      window.ConveyCurrentVideoRooms[conversationId] &&
      // @ts-ignore
      window.ConveyCurrentVideoRooms[conversationId].execLeaveRoom &&
      // @ts-ignore
      window.ConveyCurrentVideoRooms[conversationId].execLeaveRoom(conversationId);
    if (window.SDK.instances[phoneNumber].conveyVideo) {
      window.SDK.instances[phoneNumber].conveyVideo.destroy();
    }
    setVideoNoShow && setVideoNoShow();
    setRoomJoinedStatus(false);
  }

  useEffect(() => {
    if (show) {
      let transcribeEnabled = true;
      let recordEnabled = true;
      let blurEnabled = true;
      let audioEnabled = false;
      let videoEnabled = false;
      let invitationMessage = agency?.settings?.videoRequestExplainMsg || "";
      if (currentConversation.video) {
        const {
          invitationMessage: cInvitationMessage,
          transcribeEnabled: cTranscribeEnabled,
          recordEnabled: cRecordEnabled,
          videoEnabled: cVideoEnabled,
          audioEnabled: cAudioEnabled,
          blurEnabled: cBlurEnabled
        } = currentConversation.video;
        invitationMessage = cInvitationMessage;
        transcribeEnabled = cTranscribeEnabled;
        recordEnabled = cRecordEnabled;
        blurEnabled = cBlurEnabled;
        audioEnabled = cAudioEnabled;
        videoEnabled = cVideoEnabled;
      }
      (async () => {
        await delay(100);
        SDK.init({
          agencyId: app.agencyId,
          agencySecret: app.agencySecret,
          phoneNumber,
          options: app.initOptions.options
        }).videoCall({
          $container: $videoId,
          conversationId,
          invitationMessage,
          transcribe: transcribeEnabled,
          record: recordEnabled,
          blur: blurEnabled,
          enableAudio: audioEnabled,
          enableVideo: videoEnabled
        });
      })();
    }
  }, [show]);

  useEffect(() => {
    if (modalRef.current) {
      const { width, height } = modalRef.current.getBoundingClientRect();
      let modalHeight = height;
      if (!app.videoRoomMinimized && !fullScreen && height < 385) {
        modalHeight = 385;
      }
      setDragBounds({
        top: modalHeight / 2 - document.body.clientHeight / 2,
        left: width / 2 - document.body.clientWidth / 2,
        right: document.body.clientWidth / 2 - width / 2,
        bottom: document.body.clientHeight / 2 - modalHeight / 2,
      });
    }
  }, [modalRef.current, app.videoRoomMinimized, fullScreen, app.videoRoomLastActivity]);

  const modalViewOptions = (
    <Menu>
      {!fullScreen && (
        <MenuItem
          key={`${$videoId}-EnterFullscreen`}
          onClick={() => setFullScreen(true)}
        >
          <VideoCallModalViewOption>
            Enter Fullscreen
          </VideoCallModalViewOption>
        </MenuItem>
      )}
      {fullScreen && (
        <MenuItem
          key={`${$videoId}-ExitFullscreen`}
          onClick={() => setFullScreen(false)}
        >
          <VideoCallModalViewOption>
            Exit Fullscreen
          </VideoCallModalViewOption>
        </MenuItem>
      )}
      {app.videoRoomMinimized && (
        <MenuItem
          key={`${$videoId}-Maximize`}
          onClick={() => {
            setVideoRoomMaximized && setVideoRoomMaximized();
          }}
        >
          <VideoCallModalViewOption>
            Maximize
          </VideoCallModalViewOption>
        </MenuItem>
      )}
      {!app.videoRoomMinimized && (
        <MenuItem
          key={`${$videoId}-Minimize`}
          onClick={() => {
            setVideoRoomMinimized && setVideoRoomMinimized();
          }}
        >
          <VideoCallModalViewOption>
            Minimize
          </VideoCallModalViewOption>
        </MenuItem>
      )}
    </Menu>
  );

  return (
    <ReactModal
      isOpen={show}
      ariaHideApp={false}
      style={fullScreen ? VideoCallModalFullScreenStyle : VideoCallModalDefaultStyle}
      onRequestClose={() => {
        onClose();
      }}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={false}
      shouldReturnFocusAfterClose={true}
      role="dialog"
      aria={{ labelledby: "video-call-modal-title" }}
      overlayElement={(props, contentElement) => {
        return (
          <div {...props}>
            <Draggable
              cancel=".ConveyEmbed--MapWrap, .ConveyEmbed--Input, .ConveyEmbed--Textarea, .ConveyEmbed--Map3DContainer"
              positionOffset={{ x: "-50%", y: "-50%" }}
              bounds={dragBounds}
            >
              {contentElement}
            </Draggable>
          </div>
        );
      }}
      portalClassName={$videoId}
    >
      <div ref={modalRef}>
        <VideoCallModalHeader>
          <VideoCallModalTitle id="video-call-modal-title">Video Call</VideoCallModalTitle>
          <VideoCallModalActionsWrapper>
            <Dropdown
              trigger={["click"]}
              overlay={modalViewOptions}
            >
              <VideoCallModalActionButton
                data-type="Video-Call-Modal-View-Options"
                size={16}
                type="button"
                aria-label="View options"
              >
                <ModalViewOptions aria-hidden="true" />
              </VideoCallModalActionButton>
            </Dropdown>
            <VideoCallModalActionButton
              data-type="Video-Call-Modal-Video-Room-Share"
              size={16}
              type="button"
              aria-label="Share video call"
              onClick={() => openShareModal()}
            >
              <ModalShare aria-hidden="true" />
            </VideoCallModalActionButton>
            <VideoCallModalActionButton
              aria-label="Close"
              role="button"
              tabIndex={0}
              data-type="Video-Call-Modal-Close"
              size={16}
              type="button"
              aria-label="Close video call"
              onClick={() => {
                onClose();
              }}
            >
              <ModalClose aria-hidden="true" />
            </VideoCallModalActionButton>
          </VideoCallModalActionsWrapper>
        </VideoCallModalHeader>
        <VideoCallModalBody>
          <div id={$videoId} />
        </VideoCallModalBody>
      </div>
    </ReactModal>
  );
};

const mapStateToProps = ({ agency, app, conversations }: IRootState) => ({
  conversations,
  agency,
  app
});

export const VideoCallModal = connect(mapStateToProps, {
  setVideoShow,
  setVideoNoShow,
  setVideoRoomMinimized,
  setVideoRoomMaximized,
  setRoomJoinedStatus
})(XVideoCallModal);
